const STORAGE_KEY = "mittelalte.path-overrides.v1";

// Painted canvas dimensions (matches paintedPath() in maps.ts).
const PAINTED_WIDTH = 768;
const PAINTED_HEIGHT = 432;

export interface PathOverride {
  main: [number, number][];   // normalized coords (0..1; <0 or >1 = off-screen spawn allowed)
  alt?: [number, number][];   // second lane; may be independent OR end on a main vertex
}

type StoredOverrides = Record<string, PathOverride>;

function readOverrides(): StoredOverrides {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    return parsed && typeof parsed === "object" ? (parsed as StoredOverrides) : {};
  } catch (e) {
    console.warn("[path-editor] could not read path overrides", e);
    return {};
  }
}

function writeOverrides(o: StoredOverrides): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(o));
  } catch (e) {
    console.warn("[path-editor] could not save path overrides", e);
  }
}

function isTupleArray(v: unknown): v is [number, number][] {
  return (
    Array.isArray(v) &&
    v.every(pt => Array.isArray(pt) && pt.length === 2 && typeof pt[0] === "number" && typeof pt[1] === "number")
  );
}

function validateOverride(v: unknown): PathOverride | null {
  if (!v || typeof v !== "object") return null;
  const o = v as Record<string, unknown>;
  if (!isTupleArray(o["main"]) || o["main"].length < 2) return null;
  if (o["alt"] !== undefined && (!isTupleArray(o["alt"]) || (o["alt"] as unknown[]).length < 2)) return null;
  return {
    main: o["main"] as [number, number][],
    alt: o["alt"] as [number, number][] | undefined,
  };
}

export function loadPathOverride(mapKey: string): PathOverride | null {
  return validateOverride(readOverrides()[mapKey]);
}

export function savePathOverride(mapKey: string, o: PathOverride): void {
  const all = readOverrides();
  all[mapKey] = o;
  writeOverrides(all);
}

export function clearPathOverride(mapKey: string): void {
  const all = readOverrides();
  delete all[mapKey];
  writeOverrides(all);
}

/**
 * Paste-ready snippet for every stored override, formatted like paintedPath() calls in maps.ts.
 * Denormalizes to 768×432 author pixels, rounds to 1 decimal place.
 * Returns null when nothing is stored.
 */
export function exportCustomPaths(): string | null {
  const all = readOverrides();
  const keys = Object.keys(all);
  if (keys.length === 0) return null;

  const fmt = (pts: [number, number][]) =>
    pts
      .map(([u, v]) => `[${Math.round(u * PAINTED_WIDTH * 10) / 10}, ${Math.round(v * PAINTED_HEIGHT * 10) / 10}]`)
      .join(", ");

  return keys
    .map(key => {
      const o = all[key];
      let out = `  // ${key}\n  paintedWaypoints: paintedPath(${fmt(o.main)}),`;
      if (o.alt) out += `\n  altPaintedWaypoints: paintedPath(${fmt(o.alt)}),`;
      return out;
    })
    .join("\n\n");
}
